import moment from 'moment';
import _ from 'lodash';
import { Action, Dispatch } from 'redux';
import { IAppState } from '../reducers';
import {
  selectIsUserVaultSuperAdmin,
  selectUserOrgsSecurityProfiles,
} from '../user/userOrgRole';
import { pad } from '../../utils';
import { SelectOptionType } from '../../utils/selection';

import {
  IExchangeSchedule,
  getSchedules,
  getTeamSchedule,
  getTeamGameNumber,
} from '../../api/vault/exchangeSchedule';
import { IVaultTeam, getAllTeamsBySport } from '../../api/vault/teams';
import { isLacrosse } from '../../utils/exchange';

const GAME_SCHEDULE_SEASON_STORAGE_KEY = 'catapult:gameScheduleSeason';
const GAME_SCHEDULE_SPORT_STORAGE_KEY = 'catapult:gameScheduleSport';
const GAME_SCHEDULE_LEAGUE_STORAGE_KEY = 'catapult:gameScheduleLeague';
const LAST_UPLOADED_EXCHANGE_SCHEDULE_KEY = 'catapult:lastUploadedExchangeSchedule';

export enum FormType {
  Create = 'create',
  Update = 'update',
  Upload = 'upload',
  UpdateAssets = 'updateAsset',
}

export type IconMap = { [key: string]: string };
export type ITeamOption = SelectOptionType<IVaultTeam>;

export interface IState {
  formType: FormType;
  isLoaded: boolean;
  isError: boolean;
  isConference: boolean;
  seasons: Array<string>;
  schedules: Array<IExchangeSchedule>;
  exchangeSchedules: Array<IExchangeSchedule>; // Used for filtering and selection based on uploader/scouted team
  selectedSeason: string;
  selectedSchedule: IExchangeSchedule;
  selectedSport: string;
  selectedLeague: string;
  sportFilter: string;
  leagueFilter: string;
  seasonFilter: string;
  selectedExchangeQualifier: string;
  selectedExchangeVideoAngle: string;
  selectedExchangeGameType: string;
  gameDate: string;
  homeGameNumber: string;
  visitorGameNumber: string;
  isActive: boolean;
  teams: Array<IVaultTeam>;
  teamOptions: Array<ITeamOption>;
  teamIcons: IconMap;
  homeTeam: IVaultTeam;
  awayTeam: IVaultTeam;
  sourceTeam: IVaultTeam;
  scoutedTeam: IVaultTeam;
  uploader: ITeamOption;
  scoutedTeamOption: ITeamOption;
  gameId: string;
  inUseGameId: boolean;
}

export enum ActionType {
  ToggleError = 'GameSchedule.ToggleError',
  UpdateSchedules = 'GameSchedule.UpdateSchedules',
  FilterSchedules = 'GameSchedule.FilterSchedules',
  UpdateSelectedSeason = 'GameSchedule.UpdateSelectedSeason',
  UpdateSelectedSchedule = 'GameSchedule.UpdateSelectedSchedule',
  UpdateSelectedSport = 'GameSchedule.UpdateSelectedSport',
  UpdateSelectedLeague = 'GameSchedule.UpdateSelectedLeague',
  UpdateSelectedExchangeQualifier = 'GameSchedule.UpdateSelectedExchangeQualifier',
  UpdateSelectedExchangeVideoAngle = 'GameSchedule.UpdateSelectedExchangeVideoAngle',
  UpdateSelectedExchangeGameType = 'GameSchedule.UpdateSelectedExchangeGameType',
  UpdateGameDate = 'GameSchedule.UpdateGameDate',
  UpdateHomeGameNumber = 'GameSchedule.UpdateHomeGameNumber',
  UpdateVisitorGameNumber = 'GameSchedule.UpdateVisitorGameNumber',
  ToggleGameActive = 'GameSchedule.ToggleGameActive',
  UpdateHomeTeam = 'GameSchedule.UpdateHomeTeam',
  UpdateAwayTeam = 'GameSchedule.UpdateAwayTeam',
  UpdateUploader = 'GameSchedule.UpdateUploader',
  UpdateScoutedTeam = 'GameSchedule.UpdateScoutedTeam',
  UpdateGameId = 'GameSchedule.UpdateGameId',
  ToggleGameIdInUse = 'GameSchedule.ToggleGameIdInUse',
  ClearSelectedTeams = 'GameScheduke.ClearSelectedTeams',
  ClearAll = 'GameSchedule.ClearAll',
  ToggleLoading = 'GameSchedule.ToggleLoading',
  UpdateFormType = 'GameSchedule.UpdateFormType',
  UnselectCurrentSchedule = 'GameSchedule.UnselectCurrentSchedule',
}

export interface IGameScheduleAction extends Action<ActionType> {
  payload?: any;
}

const getCachedSelectedSeason = (): string => localStorage.getItem(GAME_SCHEDULE_SEASON_STORAGE_KEY);
const getCachedSelectedSport = (): string => localStorage.getItem(GAME_SCHEDULE_SPORT_STORAGE_KEY);
const getCachedSelectedLeague = (): string => localStorage.getItem(GAME_SCHEDULE_LEAGUE_STORAGE_KEY);
const getCachedLastScheduleUpload = (): string => localStorage.getItem(LAST_UPLOADED_EXCHANGE_SCHEDULE_KEY);
export const setCachedLastScheduleUpload = (id: string) => localStorage.setItem(LAST_UPLOADED_EXCHANGE_SCHEDULE_KEY, id);

const INITIAL_STATE: IState = {
  formType: FormType.Create, // Default to Create form
  isLoaded: false,
  isError: false,
  isConference: false,
  seasons: [],
  schedules: [],
  exchangeSchedules: [],
  selectedSeason: getCachedSelectedSeason(),
  selectedSport: getCachedSelectedSport(),
  selectedLeague: getCachedSelectedLeague(),
  sportFilter: null,
  leagueFilter: null,
  seasonFilter: null,
  selectedSchedule: null,
  selectedExchangeQualifier: null,
  selectedExchangeVideoAngle: null,
  selectedExchangeGameType: null,
  gameDate: null,
  homeGameNumber: null,
  visitorGameNumber: null,
  isActive: true,
  teams: [],
  teamOptions: [],
  teamIcons: {},
  homeTeam: null,
  awayTeam: null,
  sourceTeam: null,
  scoutedTeam: null,
  uploader: null,
  scoutedTeamOption: null,
  gameId: '0',
  inUseGameId: false,
};

export default function reducer(state: IState = INITIAL_STATE, action: IGameScheduleAction): IState {
  switch (action.type) {
    case ActionType.ToggleError:
      return {
        ...state,
        isLoaded: true,
        isError: true,
      };
    case ActionType.UpdateSchedules:
      return {
        ...state,
        isLoaded: true,
        seasons: action.payload.seasons,
        schedules: action.payload.schedules,
        exchangeSchedules: action.payload.exchangeSchedules,
        teams: action.payload.teams,
        teamIcons: action.payload.teamIcons,
        sourceTeam: action.payload.sourceTeam,
        scoutedTeam: action.payload.scoutedTeam,
        isConference: action.payload.isConference,
        teamOptions: action.payload.teamOptions,
        uploader: action.payload.uploader,
        scoutedTeamOption: action.payload.scoutedTeamOption,
      };
    case ActionType.UpdateSelectedSeason:
      localStorage.setItem(GAME_SCHEDULE_SEASON_STORAGE_KEY, action.payload.selectedSeason);
      return {
        ...state,
        selectedSeason: action.payload.selectedSeason,
      };
    case ActionType.UpdateSelectedSchedule:
      return {
        ...state,
        selectedSchedule: action.payload.selectedSchedule,
        gameId: action.payload.gameId,
        gameDate: action.payload.gameDate,
        homeGameNumber: action.payload.homeGameNumber,
        visitorGameNumber: action.payload.visitorGameNumber,
        selectedSport: action.payload.selectedSport,
        selectedLeague: action.payload.selectedLeague,
        selectedExchangeGameType: action.payload.selectedExchangeGameType,
        isActive: action.payload.isActive,
        homeTeam: action.payload.homeTeam,
        awayTeam: action.payload.awayTeam,
      };
    case ActionType.UnselectCurrentSchedule:
      return {
        ...state,
        selectedSchedule: null,
        gameId: '0',
        gameDate: null,
        homeGameNumber: null,
        visitorGameNumber: null,
        selectedExchangeGameType: null,
        isActive: true,
        homeTeam: null,
        awayTeam: null,
        selectedSport: null,
        selectedLeague: null,
        selectedSeason: null,
      };
    case ActionType.UpdateSelectedSport:
      localStorage.setItem(GAME_SCHEDULE_SEASON_STORAGE_KEY, action.payload.selectedSport);
      return {
        ...state,
        selectedSport: action.payload.selectedSport,
        gameId: null,
      };
    case ActionType.UpdateSelectedLeague:
      localStorage.setItem(GAME_SCHEDULE_SEASON_STORAGE_KEY, action.payload.selectedLeague);
      return {
        ...state,
        selectedLeague: action.payload.selectedLeague,
        gameId: null,
      };
    case ActionType.UpdateSelectedExchangeQualifier:
      return {
        ...state,
        selectedExchangeQualifier: action.payload.selectedExchangeQualifier,
      };
    case ActionType.UpdateSelectedExchangeVideoAngle:
      return {
        ...state,
        selectedExchangeVideoAngle: action.payload.selectedExchangeVideoAngle,
      };
    case ActionType.UpdateSelectedExchangeGameType:
      return {
        ...state,
        selectedExchangeGameType: action.payload.selectedExchangeGameType,
      };
    case ActionType.UpdateGameDate:
      return {
        ...state,
        gameDate: action.payload.gameDate,
      };
    case ActionType.UpdateHomeGameNumber:
      return {
        ...state,
        homeGameNumber: pad(action.payload.homeGameNumber),
      };
    case ActionType.UpdateVisitorGameNumber:
      return {
        ...state,
        visitorGameNumber: pad(action.payload.visitorGameNumber),
      };
    case ActionType.ToggleGameActive:
      return {
        ...state,
        isActive: !state.isActive,
      };
    case ActionType.UpdateHomeTeam:
      return {
        ...state,
        homeTeam: action.payload.homeTeam,
      };
    case ActionType.UpdateAwayTeam:
      return {
        ...state,
        awayTeam: action.payload.awayTeam,
      };
    case ActionType.UpdateUploader:
      return {
        ...state,
        uploader: action.payload.uploader,
        scoutedTeamOption: action.payload.uploader, // Copy Uploader to scoutedTeamOption
        sourceTeam: action.payload.sourceTeam,
        exchangeSchedules: action.payload.exchangeSchedules,
      };
    case ActionType.UpdateScoutedTeam:
      return {
        ...state,
        scoutedTeamOption: action.payload.scoutedTeamOption,
        scoutedTeam: action.payload.scoutedTeam,
        exchangeSchedules: action.payload.exchangeSchedules,
      };
    case ActionType.UpdateGameId:
      return {
        ...state,
        gameId: action.payload.gameId,
        inUseGameId: false,
      };
    case ActionType.ToggleGameIdInUse:
      return {
        ...state,
        inUseGameId: true,
      };
    case ActionType.ClearSelectedTeams:
      return {
        ...state,
        homeTeam: null,
        awayTeam: null,
        homeGameNumber: null,
        visitorGameNumber: null,
      };
    case ActionType.ToggleLoading:
      return {
        ...state,
        isLoaded: !state.isLoaded,
      };
    case ActionType.UpdateFormType:
      return {
        ...state,
        formType: action.payload.formType,
      };
    case ActionType.FilterSchedules:
      return {
        ...state,
        sportFilter: action.payload.sportFilter,
        leagueFilter: action.payload.leagueFilter,
        seasonFilter: action.payload.seasonFilter,
      };
    case ActionType.ClearAll:
      return {
        ...INITIAL_STATE,
        selectedSeason: getCachedSelectedSeason(),
      };
    default:
      return state;
  }
}

export const teamToOption = (team: IVaultTeam): ITeamOption => ({ label: team.TeamName, value: team });

/**
 * Seasons offered when a game can be created on the fly: previous, current and next,
 * widened for lacrosse. Exported so the form can extend its season options when the
 * sport changes mid-session, without refetching every schedule.
 */
export const newGameSeasons = (sport: string, currentYear: number = new Date().getFullYear()): string[] => (
  isLacrosse(sport)
    ? [...Array(8)].map((_, i) => (currentYear - 6 + i).toString())
    : [...Array(3)].map((_, i) => (currentYear - 1 + i).toString())
);

/**
 * Exchange upload rules - accepted file types, required pairings, qualifiers - follow
 * the sport of the game being uploaded to, NOT the sport of the user's own product.
 * A super admin whose org is Football uploading an AHL game has to get hockey rules,
 * otherwise a .xml is rejected as an unsupported file type.
 *
 * Falls back to the sport filter, then the user's product sport, for the window before
 * a game has been picked.
 */
export const selectUploadSport = (state: IAppState): string => {
  const {
    exchange: {
      gameScheduleForm: { selectedSchedule, selectedSport },
      initExchange,
    },
  } = state;
  return selectedSchedule?.Sport || selectedSport || initExchange?.sport;
};

const selectTeamSchedule = ({ TeamCode, TeamType }: IVaultTeam, exchangeSchedules: Array<IExchangeSchedule>): Array<IExchangeSchedule> => {
  if (TeamType === 'conf') {
    return exchangeSchedules;
  }
  return exchangeSchedules
    .filter(({ HomeTeamCode, VisitorTeamCode }) => TeamCode === HomeTeamCode || TeamCode === VisitorTeamCode);
};

export const queryExchangeSchedules = (upcoming: boolean = false, canUploadNewSchedule: boolean = false, isUpload: boolean = false) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const state = getState();
    const { systemSettings: { systemSettings }, exchange: { initExchange } } = state;
    const teamSportLeague = `${initExchange?.sport}:${initExchange?.currentTeam?.League}`;

    const teamsRes = await getAllTeamsBySport('all');
    const teams = (teamsRes.data?.data || []).sort((a, b) => ((a.TeamName > b.TeamName) ? 1 : -1));
    // note: We don't filter by sport here.
    // - under the assumption multiple teams with the same code will/do exist
    // - under the assumption teams wih the same team code will belong to the same school or group and will share the same icon
    // - if these assumptions are wrong, we can store the icons in the form { '${teamCode}.${sport}' } to avoid any BE changes/filtering
    // - this should also be resolved by VA-2119
    const teamIcons: IconMap = teams.reduce((container: IconMap, { _id, TeamImage }) => {
      if (!container[_id]) {
        return {
          ...container,
          [_id]: TeamImage,
        };
      }
      return container;
    }, {});
    const securityProfiles = selectUserOrgsSecurityProfiles(state);
    const sourceTeam = teams?.find(({ _id }) => securityProfiles?.find(({ team_id }) => _id === team_id));

    const { exchange: { gameScheduleForm: { selectedSport, selectedLeague } } } = state;
    if (sourceTeam && !selectedSport && !selectedLeague) {
      // note: set default league and sport if not set and found from source team
      /* eslint-disable no-use-before-define */
      dispatch(updateSelectedSport(sourceTeam.Sport));
      dispatch(updateSelectedLeague(sourceTeam.League));
      /* eslint-enable no-use-before-define */
    }

    let uploader: ITeamOption;
    const isConference = sourceTeam ? sourceTeam.TeamType === 'conf' : false;
    const teamOptions: Array<ITeamOption> = teams.map(teamToOption);

    if (isConference) uploader = teamToOption(sourceTeam);

    const isAdmin = selectIsUserVaultSuperAdmin(state);

    // VA-2259 - pull in schedule based on teamId instead of using org logic which targets team code
    // note: if conference or admin, pull all schedules
    // - if no conference detected or user is not an admin, assume they are associated with a team and pull schedule based off teamId
    // - season and sport filters applied by FE based on context of gamescheduleform component
    const schedulesRes = await ((isConference || isAdmin) ? getSchedules(false) : getTeamSchedule(sourceTeam._id));
    let schedules = schedulesRes.data?.data || [];

    const date = new Date();
    const currentYear = date.getFullYear();
    // filter for showing only home team games
    if (isUpload && systemSettings?.ExchangeHideAwayGames?.includes(teamSportLeague)) {
      schedules = schedules.filter(({ HomeTeamId }) => initExchange.currentTeam._id === HomeTeamId);
    }

    // filter out future games for specific sports leagues
    if (isUpload && systemSettings?.ExchangeHideFutureGames?.includes(teamSportLeague)) {
      schedules = schedules.filter(({ GameDate }) => new Date(GameDate) <= date);
    } else if (upcoming) {
      // note: filter exchange schedules down to future games
      schedules = schedules
        .filter(({ Season, GameDate }) => parseInt(Season, 10) >= currentYear && new Date(GameDate) > date);
    }

    const exchangeSchedules = schedules;

    let seasons: Array<string>;
    if (!upcoming) {
      const _seasons: Set<any> = schedules.reduce((source, schedule) => {
        source.add(schedule.Season);
        return source;
      }, new Set());
      seasons = [..._seasons];
      if (canUploadNewSchedule) { // Adds previous season, current season, and next season for uploading without schedule
        seasons = _.union(seasons, newGameSeasons(initExchange.sport, currentYear));
      }
    } else {
      seasons = [...Array(4)].map((_, i) => (currentYear + i).toString());
      seasons.unshift((currentYear - 1).toString());
    }

    // note: sort seasons in asc order
    seasons = seasons.sort();

    dispatch({
      type: ActionType.UpdateSchedules,
      payload: {
        seasons,
        schedules,
        exchangeSchedules,
        teams,
        teamIcons,
        sourceTeam,
        isConference,
        teamOptions,
        uploader,
      },
    });

    const {
      exchange: {
        gameScheduleForm: {
          selectedSeason,
          formType,
        },
      },
    } = state;

    if (selectedSeason && !seasons.includes(selectedSeason)) {
      dispatch({
        type: ActionType.UpdateSelectedSeason,
        payload: {
          selectedSeason: null,
        },
      });
    }

    const lastScheduleUploadId = getCachedLastScheduleUpload();
    if (lastScheduleUploadId) {
      const lastScheduleUpload = schedules.find((schedule) => schedule._id === lastScheduleUploadId);
      if (lastScheduleUpload) {
        await updateSelectedSchedule(lastScheduleUpload)(dispatch, getState);
        await updateSelectedSeason(lastScheduleUpload.Season)(dispatch, getState);
        if (formType === FormType.Update) {
          updateFilters(lastScheduleUpload.Sport, lastScheduleUpload.League, lastScheduleUpload.Season, false)(dispatch, getState);
        }
      }
    }

    return;
  } catch (e) {
    console.error('[QA] Unable to fetch exchange schedules', e.toString());
  }

  dispatch({
    type: ActionType.ToggleError,
  });
};

const unselectCurrentSchedule = () => ({
  type: ActionType.UnselectCurrentSchedule,
});

export const updateSelectedSeason = (newSelectedSeason: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const {
    exchange: {
      gameScheduleForm: {
        selectedSeason,
        selectedSchedule,
        formType,
      },
    },
  } = getState();

  if (selectedSeason !== newSelectedSeason && selectedSchedule) {
    if (formType !== FormType.Update) {
      // If season was changed unselect current schedule
      await updateSelectedSchedule(null)(dispatch, getState);
    }
  }

  dispatch({
    type: ActionType.UpdateSelectedSeason,
    payload: {
      selectedSeason: newSelectedSeason,
    },
  });
};

export const updateSelectedSchedule = (selectedSchedule?: IExchangeSchedule, teamsData?: { homeTeam?: IVaultTeam; awayTeam?: IVaultTeam }) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const {
    exchange: {
      gameScheduleForm: {
        teams,
      },
    },
  } = state;

  // note: (VA-1974) This assumes a teamcode is unique per sport/league.
  // - based on the provided sport/league we query for matching teams and find the first available with a matching code
  // - we use the matching teams to provide id's to the upload logic, which is then used by the subscription/vendor workflows

  // Normalise misses to null rather than undefined/'': a new game carries no
  // VisitorTeamId, and undefined here leaves the team selects uncontrolled
  const homeTeam = (selectedSchedule
    ? teams?.find(({ _id }) => _id === selectedSchedule.HomeTeamId)
    : null) ?? null;

  const awayTeam = (selectedSchedule
    ? teams?.find(({ _id }) => _id === selectedSchedule.VisitorTeamId)
    : null) ?? null;

  // note: backend currently returns IsActive as string instead of boolean
  let isActive = true;
  if (selectedSchedule && selectedSchedule._id) {
    isActive = typeof selectedSchedule.IsActive === 'string'
      ? JSON.parse(selectedSchedule.IsActive) : !!selectedSchedule.IsActive;
  }

  let formatedGameDate: string | null = null;
  let date = null;
  if (selectedSchedule) {
    if (selectedSchedule._id) {
      date = new Date(selectedSchedule.GameDate);
    } else {
      date = (new Date());
    }

    formatedGameDate = moment(date).startOf('day').utc().toISOString();
  }

  dispatch({
    type: ActionType.UpdateSelectedSchedule,
    payload: {
      selectedSchedule,
      gameDate: formatedGameDate,
      homeGameNumber: (selectedSchedule && selectedSchedule._id) ? selectedSchedule.HomeTeamGameNumber : '00',
      visitorGameNumber: (selectedSchedule && selectedSchedule._id) ? selectedSchedule.VisitorTeamGameNumber : '00',
      selectedSport: (selectedSchedule && selectedSchedule.Sport) ? selectedSchedule.Sport : '',
      selectedLeague: (selectedSchedule && selectedSchedule.League) ? selectedSchedule.League : '',
      selectedExchangeGameType: (selectedSchedule && selectedSchedule.GameType) ? selectedSchedule.GameType : '',
      gameId: (selectedSchedule && selectedSchedule.NCAAGameNumber) ? selectedSchedule.NCAAGameNumber : 'N/A',
      isActive,
      homeTeam: teamsData?.homeTeam || homeTeam,
      awayTeam: teamsData?.awayTeam || awayTeam,
    },
  });
};

export const updateSelectedSport = (selectedSport: string) => ({
  type: ActionType.UpdateSelectedSport,
  payload: {
    selectedSport,
  },
});

export const updateSelectedLeague = (selectedLeague: string) => ({
  type: ActionType.UpdateSelectedLeague,
  payload: {
    selectedLeague,
  },
});

/**
 * When "upload on behalf of any team" is on, sport and league act as filters over
 * every team's schedules, so changing either has to drop the currently selected
 * game - otherwise a game filtered out of the list stays silently selected.
 */

export const updateSelectedExchangeQualifier = (selectedExchangeQualifier: string) => ({
  type: ActionType.UpdateSelectedExchangeQualifier,
  payload: {
    selectedExchangeQualifier,
  },
});

export const updateSelectedExchangeVideoAngle = (selectedExchangeVideoAngle: string) => ({
  type: ActionType.UpdateSelectedExchangeVideoAngle,
  payload: {
    selectedExchangeVideoAngle,
  },
});

export const updateSelectedExchangeGameType = (selectedExchangeGameType: string) => ({
  type: ActionType.UpdateSelectedExchangeGameType,
  payload: {
    selectedExchangeGameType,
  },
});

export const updateHomeGameNumber = (homeGameNumber: string) => ({
  type: ActionType.UpdateHomeGameNumber,
  payload: {
    homeGameNumber,
  },
});

export const updateVisitorGameNumber = (visitorGameNumber: string) => ({
  type: ActionType.UpdateVisitorGameNumber,
  payload: {
    visitorGameNumber,
  },
});

export const toggleIsActive = () => ({
  type: ActionType.ToggleGameActive,
});

const updateTeamGameNumbers = async (dispatch: Dispatch, getState: () => IAppState) => {
  const state = getState();

  const {
    exchange: {
      gameScheduleForm: {
        homeTeam,
        awayTeam,
        gameDate,
        selectedSeason,
      },
    },
  } = state;

  if (gameDate && selectedSeason) {
    if (homeTeam) {
      const { data: { data: { teamGameNumber: homeGameNumber } } } = await getTeamGameNumber(homeTeam._id, selectedSeason, gameDate);
      dispatch(updateHomeGameNumber(homeGameNumber));
    }
    if (awayTeam) {
      const { data: { data: { teamGameNumber: visitorGameNumber } } } = await getTeamGameNumber(awayTeam._id, selectedSeason, gameDate);
      dispatch(updateVisitorGameNumber(visitorGameNumber));
    }
  }
};

export const updateGameDate = (gameDate: string) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const {
    exchange: {
      gameScheduleForm: {
        selectedSchedule,
      },
    },
  } = getState();
  dispatch({
    type: ActionType.UpdateGameDate,
    payload: {
      gameDate,
    },
  });

  if (!(selectedSchedule && selectedSchedule._id === null)) {
    updateTeamGameNumbers(dispatch, getState);
  }
};

export const updateHomeTeam = (option: ITeamOption) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { value: homeTeam } = option;
  const {
    exchange: {
      gameScheduleForm: {
        selectedSchedule,
      },
    },
  } = getState();

  dispatch({
    type: ActionType.UpdateHomeTeam,
    payload: {
      homeTeam,
    },
  });

  if (!(selectedSchedule && selectedSchedule._id === null)) {
    updateTeamGameNumbers(dispatch, getState);
  }
};

export const updateAwayTeam = (option: ITeamOption) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { value: awayTeam } = option;
  const {
    exchange: {
      gameScheduleForm: {
        selectedSchedule,
      },
    },
  } = getState();

  dispatch({
    type: ActionType.UpdateAwayTeam,
    payload: {
      awayTeam,
    },
  });

  if (!(selectedSchedule && selectedSchedule._id === null)) {
    updateTeamGameNumbers(dispatch, getState);
  }
};

export const updateUploader = (uploader: ITeamOption) => (dispatch: Dispatch, getState: () => IAppState) => {
  const state = getState();

  const {
    exchange: {
      gameScheduleForm: {
        schedules,
      },
    },
  } = state;

  const {
    value: sourceTeam,
  } = uploader;

  const exchangeSchedules = selectTeamSchedule(sourceTeam, schedules);

  dispatch({
    type: ActionType.UpdateUploader,
    payload: {
      uploader,
      sourceTeam,
      exchangeSchedules,
    },
  });
};

export const updateScoutedTeam = (scoutedTeamOption: ITeamOption) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();

  const {
    exchange: {
      gameScheduleForm: {
        schedules,
      },
    },
  } = state;

  const {
    value: scoutedTeam,
  } = scoutedTeamOption;

  const exchangeSchedules = selectTeamSchedule(scoutedTeam, schedules);

  dispatch({
    type: ActionType.UpdateScoutedTeam,
    payload: {
      scoutedTeamOption,
      scoutedTeam,
      exchangeSchedules,
    },
  });
};

export const updateGameId = (gameId: string) => ({
  type: ActionType.UpdateGameId,
  payload: {
    gameId,
  },
});

export const toggleGameIdInUse = () => ({
  type: ActionType.ToggleGameIdInUse,
});

export const clearSelectedTeams = () => ({
  type: ActionType.ClearSelectedTeams,
});

export const clearAll = () => ({
  type: ActionType.ClearAll,
});

export const selectTeamOptions = (state: IAppState, noConf: boolean = false): Array<ITeamOption> => {
  const {
    exchange: {
      gameScheduleForm: {
        selectedSport,
        selectedLeague,
        teamOptions,
      },
    },
  } = state;
  if (selectedSport && selectedLeague) {
    return teamOptions.filter(({ value: { Sport, League, TeamType } }) => selectedSport === Sport && selectedLeague === League && (noConf ? TeamType !== 'conf' : true));
  }
  return teamOptions;
};

export const ToggleLoading = () => ({
  type: ActionType.ToggleLoading,
});

export const queryTeamsForCreate = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const state = getState();
    const teamsRes = await getAllTeamsBySport('all');
    const teams = (teamsRes.data?.data || []).sort((a, b) => ((a.TeamName > b.TeamName) ? 1 : -1));
    const teamIcons: IconMap = teams.reduce((container: IconMap, { _id, TeamImage }) => {
      if (!container[_id]) {
        return {
          ...container,
          [_id]: TeamImage,
        };
      }
      return container;
    }, {});
    const securityProfiles = selectUserOrgsSecurityProfiles(state);
    const sourceTeam = teams?.find(({ _id }) => securityProfiles?.find(({ team_id }) => _id === team_id));
    const isConference = sourceTeam ? sourceTeam.TeamType === 'conf' : false;
    const teamOptions: Array<ITeamOption> = teams.map(teamToOption);
    let uploader: ITeamOption;
    if (isConference) uploader = teamToOption(sourceTeam);

    const currentYear = new Date().getFullYear();
    const seasons = [...Array(3)].map((_, i) => (currentYear - 1 + i).toString()).sort();

    dispatch({
      type: ActionType.UpdateSchedules,
      payload: {
        seasons,
        schedules: [],
        exchangeSchedules: [],
        teams,
        teamIcons,
        sourceTeam,
        isConference,
        teamOptions,
        uploader,
      },
    });
  } catch (e) {
    console.error('[QA] Unable to fetch teams for create', e.toString());
    dispatch({ type: ActionType.ToggleError });
  }
};

export const updateFormType = (formType: FormType) => ({
  type: ActionType.UpdateFormType,
  payload: {
    formType,
  },
});

export const updateFilters = (sportFilter: string, leagueFilter: string, seasonFilter: string, resetSchedule: boolean = true) => (dispatch: Dispatch, getState: () => IAppState) => {
  if (resetSchedule) dispatch(unselectCurrentSchedule());
  dispatch({
    type: ActionType.FilterSchedules,
    payload: {
      sportFilter,
      leagueFilter,
      seasonFilter,
    },
  });
};

export const updateSingleSchedule = (schedule: IExchangeSchedule) => (dispatch: Dispatch, getState: () => IAppState) => {
  const state = getState();
  const {
    exchange: {
      gameScheduleForm: {
        schedules,
        seasons,
        exchangeSchedules,
        teams,
        teamIcons,
        sourceTeam,
        scoutedTeam,
        isConference,
        teamOptions,
        uploader,
        scoutedTeamOption,
      },
    },
  } = state;

  const foundIndex = schedules.findIndex((s) => s._id === schedule._id);
  const updatedSchedules = [...schedules];
  updatedSchedules[foundIndex] = schedule;
  const updateExchangeSchedules = [...exchangeSchedules];
  const foundExchangeIndex = exchangeSchedules.findIndex((s) => s._id === schedule._id);
  updateExchangeSchedules[foundExchangeIndex] = schedule;

  dispatch({
    type: ActionType.UpdateSchedules,
    payload: {
      seasons,
      schedules: updatedSchedules,
      exchangeSchedules: updateExchangeSchedules,
      teams,
      teamIcons,
      sourceTeam,
      scoutedTeam,
      isConference,
      teamOptions,
      uploader,
      scoutedTeamOption,
    },
  });
  updateSelectedSchedule(schedule)(dispatch, getState);
  updateFilters(schedule.Sport, schedule.League, schedule.Season, false)(dispatch, getState);
};
